import { env } from "@src/env.mjs";
import { Database } from "types/schema";
import { authClient } from "./authClient";

const serverUrl = env.NEXT_PUBLIC_SERVER_URL;

type FilterOp =
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "like"
  | "ilike"
  | "is"
  | "in"
  | "contains"
  | "containedBy"
  | "overlaps";

interface Filter {
  field: string;
  op: FilterOp;
  value: any;
}

interface OrderClause {
  column: string;
  ascending?: boolean;
  nullsFirst?: boolean;
}

interface DataProxyRequest {
  table: string;
  operation: "select" | "insert" | "update" | "upsert" | "delete";
  columns?: string;
  data?: Record<string, any> | Record<string, any>[];
  filters?: Filter[];
  orFilters?: string[];
  order?: OrderClause[];
  range?: { from: number; to: number };
  single?: boolean;
  count?: "exact" | "planned" | "estimated";
  softDelete?: boolean;
  selectDeleted?: boolean;
  textSearch?: { column: string; query: string; type?: "plain" | "phrase" | "websearch" };
  limit?: number;
  matchFilter?: Record<string, any>;
}

interface QueryResult<T = any> {
  data: T | null;
  error: { message: string } | null;
  count: number | null;
}

async function executeQuery(payload: DataProxyRequest): Promise<QueryResult> {
  const token = await authClient.getAccessToken();
  const res = await fetch(`${serverUrl}/data`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  const json = await res.json();
  return {
    data: json.data ?? null,
    error: json.error ? { message: json.error } : null,
    count: json.count ?? null,
  };
}

/**
 * Chainable query builder that mirrors the Supabase client API.
 * Collects query parameters and sends them to the server data proxy on execution.
 */
class QueryBuilder<T = any> {
  private payload: DataProxyRequest;
  private _then?: (result: QueryResult<T>) => any;

  constructor(table: string) {
    this.payload = { table, operation: "select" };
  }

  select(columns?: string, opts?: { count?: "exact" | "planned" | "estimated" }): this {
    // Only set operation to "select" if no mutation operation has been set.
    // When chained after .insert()/.update()/.upsert(), .select() means
    // "return the result" — not "switch to a select query".
    if (this.payload.operation === "select" || !this.payload.data) {
      this.payload.operation = "select";
    }
    if (columns) this.payload.columns = columns;
    if (opts?.count) this.payload.count = opts.count;
    return this;
  }

  insert(data: Record<string, any> | Record<string, any>[]): this {
    this.payload.operation = "insert";
    this.payload.data = data;
    return this;
  }

  update(data: Record<string, any>): this {
    this.payload.operation = "update";
    this.payload.data = data;
    return this;
  }

  upsert(data: Record<string, any> | Record<string, any>[]): this {
    this.payload.operation = "upsert";
    this.payload.data = data;
    return this;
  }

  delete(): this {
    this.payload.operation = "delete";
    return this;
  }

  // Filter methods
  eq(column: string, value: any): this {
    this.addFilter(column, "eq", value);
    return this;
  }

  neq(column: string, value: any): this {
    this.addFilter(column, "neq", value);
    return this;
  }

  gt(column: string, value: any): this {
    this.addFilter(column, "gt", value);
    return this;
  }

  gte(column: string, value: any): this {
    this.addFilter(column, "gte", value);
    return this;
  }

  lt(column: string, value: any): this {
    this.addFilter(column, "lt", value);
    return this;
  }

  lte(column: string, value: any): this {
    this.addFilter(column, "lte", value);
    return this;
  }

  like(column: string, value: string): this {
    this.addFilter(column, "like", value);
    return this;
  }

  ilike(column: string, value: string): this {
    this.addFilter(column, "ilike", value);
    return this;
  }

  is(column: string, value: null | boolean): this {
    this.addFilter(column, "is", value);
    return this;
  }

  in(column: string, values: any[]): this {
    this.addFilter(column, "in", values);
    return this;
  }

  contains(column: string, value: any): this {
    this.addFilter(column, "contains", value);
    return this;
  }

  containedBy(column: string, value: any): this {
    this.addFilter(column, "containedBy", value);
    return this;
  }

  overlaps(column: string, value: any): this {
    this.addFilter(column, "overlaps", value);
    return this;
  }

  or(filters: string): this {
    if (!this.payload.orFilters) this.payload.orFilters = [];
    this.payload.orFilters.push(filters);
    return this;
  }

  filter(column: string, op: string, value: any): this {
    this.addFilter(column, op as FilterOp, value);
    return this;
  }

  match(filter: Record<string, any>): this {
    this.payload.matchFilter = { ...this.payload.matchFilter, ...filter };
    return this;
  }

  textSearch(column: string, query: string, opts?: { type?: "plain" | "phrase" | "websearch" }): this {
    this.payload.textSearch = { column, query, type: opts?.type };
    return this;
  }

  // Modifiers
  order(column: string, opts?: { ascending?: boolean; nullsFirst?: boolean }): this {
    if (!this.payload.order) this.payload.order = [];
    this.payload.order.push({
      column,
      ascending: opts?.ascending,
      nullsFirst: opts?.nullsFirst,
    });
    return this;
  }

  range(from: number, to: number): this {
    this.payload.range = { from, to };
    return this;
  }

  limit(count: number): this {
    this.payload.limit = count;
    return this;
  }

  single(): this {
    this.payload.single = true;
    return this;
  }

  // Type-only cast (mirrors Supabase's .returns<T>() which has no runtime effect)
  returns<T>(): QueryBuilder<T> {
    return this as unknown as QueryBuilder<T>;
  }

  // Soft delete support (matching existing supabaseClient.ts pattern)
  softDelete(extraData?: Record<string, any>): this {
    this.payload.operation = "delete";
    this.payload.softDelete = true;
    if (extraData) this.payload.data = extraData;
    return this;
  }

  // Control deleted row visibility
  showDeleted(show: boolean = true): this {
    this.payload.selectDeleted = show;
    return this;
  }

  // --- Execution ---

  private addFilter(field: string, op: FilterOp, value: any): void {
    if (!this.payload.filters) this.payload.filters = [];
    this.payload.filters.push({ field, op, value });
  }

  /**
   * Execute the query and return the result.
   * Also implements PromiseLike so you can `await` the builder directly.
   */
  async execute(): Promise<QueryResult<T>> {
    return executeQuery(this.payload) as Promise<QueryResult<T>>;
  }

  /**
   * PromiseLike interface — allows `await supabase.from("x").select("*")`
   * without explicit `.execute()`.
   */
  then<TResult1 = QueryResult<T>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

/**
 * Server-proxied data client that mirrors the Supabase client API.
 * Use `supabase.from("table")` to get a chainable query builder.
 */
class ServerDataClient {
  from<Table extends keyof Database["public"]["Tables"]>(
    table: Table
  ): QueryBuilder<Database["public"]["Tables"][Table]["Row"][]> {
    return new QueryBuilder(table as string);
  }
}

// Export as a drop-in replacement for the Supabase client
export const supabase = new ServerDataClient();

// Re-export helpers matching the original supabaseClient.ts API
export const getPagination = (page: number, size: number) => {
  const limit = size ? +size : 3;
  const from = page ? page * limit : 0;
  const to = (page ? from + size : size) - 1;
  return { from, to };
};

export type SelectDataProps<Table, Query> = {
  selectDeleted?: boolean;
  table: Table;
  columns?: Query;
};

export function selectData<
  Table extends keyof Database["public"]["Tables"],
  Query extends string = "*",
>({ selectDeleted = false, table, columns }: SelectDataProps<Table, Query>) {
  const builder = new QueryBuilder(table as string).select(columns ?? "*");
  // Server-side proxy auto-applies deleted != true filter,
  // so we only need to tell it if we want deleted rows included
  if (selectDeleted) {
    builder.showDeleted(true);
  }
  return builder;
}

export function softDelete<Table extends keyof Database["public"]["Tables"]>({
  table,
  updateData,
}: {
  table: Table;
  updateData?: Record<string, any>;
}) {
  const builder = new QueryBuilder(table as string);
  builder.update({ ...(updateData ?? {}), deleted: true });
  return builder;
}
